// This is api.js

  // ^^^^^^^ WORKING API FUNCTION  ^^^^^  

   export const createStripePaymentLink = async (idToken) => {
       const res = await fetch('https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/create-payment-link', {
         method: 'POST',
         headers: {
           Authorization: `Bearer ${idToken}`,
         },
       });
       if (!res.ok) throw new Error('Failed to create Stripe payment link');
       const { url } = await res.json();
       return url;
     };

  
const buildSecDataPath = (prefix, symbol, fileType) =>
  `/${prefix}/${encodeURIComponent(symbol)}_${fileType}.json`;

const buildLegacySecDataPath = (prefix, symbol) =>
  `/${prefix}/${encodeURIComponent(symbol)}.json`;

const buildFinancialRatiosPaths = (symbol) => [
  buildSecDataPath('financial-ratios', symbol, 'financial_ratios'),
  buildLegacySecDataPath('financial-ratios', symbol)
];

const buildIncomeStatementPaths = (symbol) => [
  buildSecDataPath('income-statements', symbol, 'income_statement'),
  buildLegacySecDataPath('income-statements', symbol)
];

const fetchJsonFromPaths = async (paths, datasetLabel, symbol, { signal, missingOnly = false } = {}) => {
  const failures = [];
  let missing = true;

  for (const path of paths) {
    try {
      const res = await fetch(path, { signal });
      const text = await res.text();

      if (!res.ok) {
        failures.push(`${path}: HTTP ${res.status}`);
        // Private S3 origins can return 403 for absent keys.
        if (![403, 404].includes(res.status)) {
          missing = false;
          if (missingOnly) break;
        }
        continue;
      }

      missing = false;
      try {
        return JSON.parse(text);
      } catch (err) {
        failures.push(`${path}: invalid JSON`);
        if (missingOnly) break;
      }
    } catch (err) {
      if (signal?.aborted || err.name === 'AbortError') throw err;
      missing = false;
      failures.push(`${path}: ${err.message}`);
      if (missingOnly) break;
    }
  }

  const error = new Error(`Failed to fetch ${datasetLabel} for ${symbol}`);
  error.code = missing ? 'MISSING_DATA' : 'FETCH_FAILED';
  error.failures = failures;
  throw error;
};

export const fetchFinancialRatios = async (symbol, { signal } = {}) => {
  const normalizedSymbol = symbol?.trim().toUpperCase();

  if (!normalizedSymbol) {
    throw new Error('A stock symbol is required to fetch financial ratios');
  }

  return fetchJsonFromPaths(
    buildFinancialRatiosPaths(normalizedSymbol),
    'financial ratios data',
    normalizedSymbol,
    { signal, missingOnly: true }
  );
};

const validDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === value;
};

export const fetchLandingTopline = async ({ signal } = {}) => {
  const response = await fetch('/landing/landing_page_topline.json', { signal });
  if (!response.ok) throw new Error('Market snapshot is unavailable');
  const snapshot = await response.json();
  if (!snapshot || typeof snapshot.updatedAt !== 'string'
      || !Number.isFinite(Date.parse(snapshot.updatedAt)) || !Array.isArray(snapshot.rows)) {
    throw new Error('Invalid market snapshot');
  }
  const seen = new Set();
  const rows = snapshot.rows.map(row => {
    if (!row || typeof row.symbol !== 'string' || !/^[A-Z][A-Z0-9.-]{0,9}$/.test(row.symbol) || seen.has(row.symbol)) {
      throw new Error('Invalid market snapshot row');
    }
    seen.add(row.symbol);
    const normalized = { ...row, name: typeof row.name === 'string' ? row.name : row.symbol };
    ['price', 'changePercent', 'marketCap', 'volume', 'dayLow', 'dayHigh'].forEach(key => {
      normalized[key] = typeof row[key] === 'number' && Number.isFinite(row[key]) ? row[key] : null;
    });
    return normalized;
  });
  return { ...snapshot, rows };
};

// Canonical tie-breaker makes duplicate-period selection independent of file order.
const canonicalJson = (value) => JSON.stringify(value, (_key, item) =>
  item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]]))
    : item
);

/**
 * Resolves { status: 'ready', data } or { status: 'unavailable', reason, data: null }.
 * Transport/invalid-file failures reject; cancellations remain AbortErrors.
 * FY rows require a real period-end date. No older-period field backfilling occurs.
 */
export const fetchLatestFinancialRatios = async (symbol, options = {}) => {
  let rows;
  try {
    rows = await fetchFinancialRatios(symbol, options);
  } catch (error) {
    if (error.code !== 'MISSING_DATA') throw error;
    return { status: 'unavailable', reason: 'missing', data: null };
  }
  if (!Array.isArray(rows)) throw new Error('Financial ratios must be an array');
  const annualRows = rows.filter(row => row?.period === 'FY' && validDate(row.date));
  annualRows.sort((a, b) => {
    const aRank = [a.date, String(a.fiscalYear || ''), validDate(a.filingDate) ? a.filingDate : '', canonicalJson(a)];
    const bRank = [b.date, String(b.fiscalYear || ''), validDate(b.filingDate) ? b.filingDate : '', canonicalJson(b)];
    for (let i = 0; i < aRank.length; i += 1) {
      if (aRank[i] !== bRank[i]) return aRank[i] > bRank[i] ? -1 : 1;
    }
    return 0;
  });
  return annualRows.length
    ? { status: 'ready', data: annualRows[0] }
    : { status: 'unavailable', reason: rows.length ? 'no_annual_period' : 'empty', data: null };
};

  export const fetchHistoricalData = async (symbol) => {
    const normalizedSymbol = symbol?.trim().toUpperCase();

    if (!normalizedSymbol) {
      throw new Error('A stock symbol is required to fetch historical data');
    }

    const res = await fetch(`/ohlc/${normalizedSymbol}/${normalizedSymbol}_historical_viz_ohlc.json`);

    if (!res.ok) {
      throw new Error(`Failed to fetch historical data for ${normalizedSymbol}`);
    }

    const text = await res.text();

    try {
      const data = JSON.parse(text);

      if (Array.isArray(data)) {
        return data;
      }

      console.error('Unexpected historical data shape:', data);
      return [];
    } catch (err) {
      console.error(`Invalid historical JSON for ${normalizedSymbol}:`, text.slice(0, 200));
      throw new Error(`Historical response for ${normalizedSymbol} is not valid JSON`);
    }
  };
  


export const fetchStockList = async (idToken) => {
      const headers = {};
      if (idToken) {
        headers.Authorization = `Bearer ${idToken}`;
      }
      const res = await fetch(
        "https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/list-stocks-search",
        { headers }
      );
      if (!res.ok) throw new Error("Failed to fetch stock list");
      return res.json(); // array of { symbol, name }
    };

const fetchSecIncomeStatementFile = async (symbol, growth, { signal } = {}) => {
  const normalizedSymbol = symbol?.trim().toUpperCase();
  const datasetLabel = growth ? 'income statement growth' : 'income statement';
  if (!normalizedSymbol) {
    throw new Error(`A stock symbol is required to fetch ${datasetLabel} data`);
  }

  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timeout = setTimeout(abort, 15000);
  try {
    const result = await fetchJsonFromPaths(
      growth ? [buildSecDataPath('income-statements', normalizedSymbol, 'income_statement_growth')]
        : buildIncomeStatementPaths(normalizedSymbol),
      datasetLabel, normalizedSymbol, { signal: controller.signal, missingOnly: true }
    );
    if (!growth) {
      if (!Array.isArray(result) || result.some(row => !row || typeof row !== 'object')) {
        throw new Error('Invalid income statement data');
      }
    } else if (!result || result.schemaVersion !== 1 || result.formulaVersion !== 'sec-annual-growth-v1'
        || result.symbol !== normalizedSymbol || !/^[a-f0-9]{64}$/.test(result.sourceStatementVersion)
        || !['ready', 'unavailable'].includes(result.status)
        || !Array.isArray(result.rows) || result.rows.some(row => !row || row.symbol !== normalizedSymbol
          || row.period !== 'FY' || !validDate(row.date) || !/^\d{4}$/.test(String(row.fiscalYear))
          || Object.entries(row).some(([key, value]) => (key.startsWith('growth') || key === 'grossProfitMarginChange')
            && value !== null && (typeof value !== 'number' || !Number.isFinite(value))))) {
      throw new Error('Invalid SEC income statement growth data');
    }
    return result;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
};

export const fetchIncomeStatement = (symbol, options) =>
  fetchSecIncomeStatementFile(symbol, false, options);

export const fetchIncomeStatementGrowth = (symbol, options) =>
  fetchSecIncomeStatementFile(symbol, true, options);
